import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DropdownButton } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/courses.action'
import { DropdownLoader } from 'components/ui/DropdownLoader'
import { useAccess } from 'contexts/AccessContext'
import { createRouteMonitoringCourse } from 'enum/routes'
import getNormativeParticipantsDataValidation from 'api/administration/courses/GetNormativeParticipantsDataValidation'
import { formatThousandsSeparator } from 'helpers/others.helper'
import { useNotification } from 'hooks/notifications/useNotification'
import {
  Dc3CertificateGenerateModal,
  Dc3GenerationMode,
} from 'components/dc3Certificate'
import styles from './Options.module.scss'

interface IProps {
  id: any
  name: any
  onDeleteClick: any
  onDuplicateClick: any
  integrationCourseLink: string
  synchronous: boolean
  normative: boolean
  dc3AttestationAssigned?: boolean
  onShowNormativeIncompleteModal: (
    incompleteCount: number,
    courseId: number
  ) => void
}

const normativeValidationNotificationKey = (courseId: number) =>
  `normative-participants-validation-${courseId}`

const Options: React.FC<IProps> = ({
  id,
  name,
  onDeleteClick,
  onDuplicateClick,
  integrationCourseLink,
  synchronous,
  normative,
  dc3AttestationAssigned = false,
  onShowNormativeIncompleteModal,
}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const notification = useNotification()
  const attestationsEnabled = useSelector(
    ({ general }: IMainState) => general.permissions.attestationsEnabled
  )
  const [validatingNormativeData, setValidatingNormativeData] = useState(false)
  const [generateModalOpen, setGenerateModalOpen] = useState(false)
  const [generateMode, setGenerateMode] =
    useState<Dc3GenerationMode>('download')

  const { can, canAccessCourse, loading, access } = useAccess()
  const [show, setShow] = useState(false)

  const validationPrefix =
    'administration.courses.list.normativeDataValidation.notifications'

  const hasCourseScope = canAccessCourse(Number(id))
  // Esta lista contiene cursos LMS internos; CourseType solo distingue abierto/cerrado.
  const canManageCourseActions = hasCourseScope && can('manageInternalCourses')

  const handleDropdownClick = (showDropdown: boolean) => {
    setShow(showDropdown)
  }

  const handleDownload = (courseId: number) => {
    setShow(false)
    dispatch(fromActions.Actions.coursesIndividualDownload({ courseId }))
  }

  const handleValidateNormativeData = async () => {
    const notificationKey = normativeValidationNotificationKey(id)
    setShow(false)
    setValidatingNormativeData(true)
    notification.loading(t(`${validationPrefix}.validating`), notificationKey)

    try {
      const {
        participantsTotal,
        incompleteParticipantsCount: incompleteCount,
      } = await getNormativeParticipantsDataValidation(id)

      notification.close(notificationKey)

      if (!incompleteCount) {
        const compliantMessage =
          participantsTotal === 1
            ? t(`${validationPrefix}.allParticipantsCompliantOne`)
            : t(`${validationPrefix}.allParticipantsCompliantMany`, {
                count: participantsTotal,
              }).replace(
                String(participantsTotal),
                formatThousandsSeparator(participantsTotal, i18n.language)
              )

        notification.success(compliantMessage)
      } else {
        onShowNormativeIncompleteModal(incompleteCount, id)
      }
    } catch {
      notification.close(notificationKey)
      notification.error(t(`${validationPrefix}.error`))
    } finally {
      setValidatingNormativeData(false)
    }
  }

  const canDownloadReport =
    !integrationCourseLink &&
    hasCourseScope &&
    access.canDownloadReportWithViewAccess()
  const canValidateNormativeData =
    attestationsEnabled &&
    normative &&
    hasCourseScope &&
    can('manageParticipants')
  const showDownloadLegalAttestation =
    attestationsEnabled && normative === true && canDownloadReport
  const downloadLegalAttestationDisabled =
    !dc3AttestationAssigned || dc3AttestationAssigned !== true

  const handleDownloadLegalAttestation = () => {
    setShow(false)
    setGenerateMode('zip')
    setGenerateModalOpen(true)
  }

  const canSeeMonitoring = hasCourseScope && access.canAccessCourseSheet()
  const canPreview = hasCourseScope && access.canPreviewCourse()
  const canEditCourse = hasCourseScope && access.canEditCourseFromList()

  const hasVisibleOptions =
    canSeeMonitoring ||
    canPreview ||
    canEditCourse ||
    canManageCourseActions ||
    canDownloadReport ||
    canValidateNormativeData ||
    showDownloadLegalAttestation

  return (
    <>
      <div
        className={show ? styles.menuOpen : undefined}
        data-menu-open={show ? 'true' : undefined}
      >
        <DropdownButton
          show={hasVisibleOptions && show}
          disabled={!hasVisibleOptions || loading}
          id="dropdown-menu-align-right"
          className={styles.actionSelect}
          alignRight
          title={<DropdownLoader loading={loading} />}
          onClick={() => {
            if (hasVisibleOptions) handleDropdownClick(!show)
          }}
          onToggle={(nextShow) => {
            if (hasVisibleOptions) handleDropdownClick(nextShow)
          }}
        >
          {hasVisibleOptions && show && (
            <>
              {canSeeMonitoring && (
                <a
                  onClick={() => {
                    const url = createRouteMonitoringCourse({
                      id,
                    })
                    history.push(url)
                  }}
                >
                  {t(
                    'administration.courses.list.create.table.headers.actions.seeCourseSheet',
                    {
                      defaultValue: t(
                        'administration.courses.list.create.table.headers.actions.seeMonitoring'
                      ),
                    }
                  )}
                </a>
              )}
              {canPreview && (
                <a
                  href={`/administration/courses/${id}/preview`}
                  target="_blank"
                  className={
                    integrationCourseLink || synchronous
                      ? styles.optionDisabled
                      : ''
                  }
                >
                  {t('administration.lessons.buttons.preview')}
                </a>
              )}
              {canEditCourse && (
                <a
                  onClick={() =>
                    history.push(`/administration/courses/${id}/edit`)
                  }
                >
                  {t(
                    'administration.courses.list.create.table.headers.actions.edit'
                  )}
                </a>
              )}
              {canManageCourseActions && (
                <a onClick={() => onDeleteClick(id || 0, name)}>
                  {t(
                    'administration.courses.list.create.table.headers.actions.delete'
                  )}
                </a>
              )}
              {canManageCourseActions && (
                <a
                  onClick={() => onDuplicateClick(id || 0)}
                  className={integrationCourseLink ? styles.optionDisabled : ''}
                >
                  {t(
                    'administration.courses.list.create.table.headers.actions.duplicate'
                  )}
                </a>
              )}
              {canDownloadReport && (
                <a onClick={() => handleDownload(id)}>
                  {t(
                    'administration.courses.list.create.table.headers.actions.report'
                  )}
                </a>
              )}
              {canValidateNormativeData && (
                <a
                  className={
                    validatingNormativeData ? styles.optionDisabled : ''
                  }
                  onClick={() => {
                    if (!validatingNormativeData) {
                      handleValidateNormativeData()
                    }
                  }}
                >
                  {t(
                    'administration.courses.list.create.table.headers.actions.validateNormativeData'
                  )}
                </a>
              )}
              {showDownloadLegalAttestation && (
                <a
                  className={
                    downloadLegalAttestationDisabled
                      ? styles.optionDisabled
                      : ''
                  }
                  onClick={() => {
                    if (!downloadLegalAttestationDisabled) {
                      handleDownloadLegalAttestation()
                    }
                  }}
                >
                  {t(
                    'administration.courses.list.create.table.headers.actions.downloadLegalAttestation'
                  )}
                </a>
              )}
            </>
          )}
        </DropdownButton>
      </div>
      {generateModalOpen ? (
        <Dc3CertificateGenerateModal
          open={generateModalOpen}
          mode={generateMode}
          courseId={id}
          zipFileName={t('dc3Certificate.generateModal.courseZipFileName', {
            courseName: name,
          })}
          onClose={() => setGenerateModalOpen(false)}
        />
      ) : null}
    </>
  )
}

export default Options
